package com.milta.feature.ocrscan.domain.entity

import com.milta.feature.route.domain.entity.GeocodeFailureReason
import com.milta.feature.route.domain.entity.GeocodeStatus

/**
 * One delivery row as read off a driver-photographed paper delivery list (`POST /api/v1/ocr-scans`).
 * Nothing is persisted at this stage — every field is raw, unvalidated OCR output; the driver
 * reviews/corrects the rows client-side, then confirms via `POST /routes/{routeId}/deliveries`.
 *
 * [hasMissingFields] is a single opaque boolean from the backend, without detail on *which* field
 * is missing. Screens C/D deliberately do NOT rely on it to decide whether a row blocks the batch
 * confirmation — they check [recipientName]/[address] nullity directly instead, since those are the
 * only two fields the confirmation endpoint actually requires (see [OcrScanResult] mapping in the
 * presentation layer).
 */
data class ScannedDelivery(
    val recipientName: String?,
    val address: String?,
    val phoneNumber: String?,
    val deadlineTime: String?,
    val hasMissingFields: Boolean,
    /** Parcel count read by the OCR (always >= 1, backend default 1) — MIL-88: no longer dropped. */
    val packageCount: Int = 1,
    /** HERE validation outcome of [address], resolved server-side before this row reached the client. */
    val geocodeStatus: GeocodeStatus = GeocodeStatus.Pending,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /** Retour 26/10 (R1/R11) — why HERE refused [address] (Failed only), null otherwise. */
    val geocodeFailureReason: GeocodeFailureReason? = null,
    /** Retour 29/10 — the address HERE matched ("Trouvée : …"), null when not reported (older data). */
    val resolvedAddress: String? = null,
)
